import { createSlice } from "@reduxjs/toolkit";
import {
  getPredifinedPanelsByFilter,
  getPredifinedPanelSubcategoriesByCategoryId,
  setPredifinedPanelCategorySelectState,
  setPredifinedPanelCategoryUnSelectState,
  getPredifinedPanelCategories,
  getTableConfigurationByPanelId,
  getPieConfigurationByPanelId,
  getXyChartConfigurationByPanelId,
  getConsumeDataByPanelId,
  getKpiConfigurationByPanelId,
} from "../../../modules/CustomDashboardModule/services/DashboardConfigurationService";
import { actions as loaderActions } from "../loader/loaderSlice";

const initialState = {
  page: 0,
  stream: undefined,
  tabId: undefined,
  categories: [],
  subcategories: [],
  panels: [],
  selectedPanels: [],
  panelLoadingMap: {}, // Map to track loading state of individual panels
  loading: false,
  error: null,
};

export const accountSlice = createSlice({
  name: "account",
  initialState,
  reducers: {
    incrementPage(state) {
      state.page += 1;
    },
    setCategories(state, action) {
      state.categories = action.payload;
      //check if category is selected
    },
    setSelectCategory(state, action) {
      const { categoryId, isSelected } = action.payload;
      state.categories.forEach((c) => {
        if (c.id === categoryId) {
          c.isSelected = isSelected;
        }
      });

      if (!isSelected) {
        state.subcategories = state.subcategories.filter(
          (subcategory) => subcategory.categoryId !== categoryId
        );
      }
    },
    setSelectSubcategory(state, action) {
      const { subcategoryId, isSelected } = action.payload;
      state.subcategories.forEach((group) => {
        group.subCategories.forEach((s) => {
          if (s.id === subcategoryId) {
            s.isSelected = isSelected;
          }
        });
      });
    },
    setSubCategories(state, action) {
      const category = state.categories.find(
        (c) => c.id === action.payload.categoryId
      );
      const group = {
        category,
        ...action.payload,
      };
      state.subcategories.push(group);
    },
    setPanels(state, action) {
      state.panels = state.panels.concat(action.payload);
    },
    setSelectedPanels(state, action) {
      state.selectedPanels = action.payload;
    },
    updatePanel(state, action) {
      const { panelId, configuration } = action.payload;
      const updatedPanel = state.panels.find((panel) => panel.id === panelId);
      if (!updatedPanel) return;
      updatedPanel.configuration = configuration;
    },
    toggleSelectedPanel(state, action) {
      const { selectedPanel } = action.payload;
      state.selectedPanels = state.selectedPanels.find(
        (p) => p.id === selectedPanel.id
      )
        ? state.selectedPanels.filter((panel) => panel.id !== selectedPanel.id)
        : [...state.selectedPanels, selectedPanel];
    },
    setTabMetadata(state, action) {
      const { stream, tabId } = action.payload;
      state.stream = stream;
      state.tabId = tabId;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    reset(state) {
      state.page = 0;
      state.categories = [];
      state.subcategories = [];
      state.panels = [];
      state.loading = false;
      state.error = null;
      state.stream = undefined;
      state.tabId = undefined;
    },
    setPanelLoading(state, action) {
      const { panelId, status } = action.payload;
      state.panelLoadingMap[panelId] = status;
    },
    initializedLoadingMap(state, _) {
      const panels = state.panels;
      state.panelLoadingMap = panels.reduce((loadingMap, panel) => {
        loadingMap[panel.id] = "not-loaded";
        return loadingMap;
      }, {});
    },
  },
});

export const actions = accountSlice.actions;

const selectAccount = (state) => state.account;
const selectAccountCategories = (state) => state.account.categories;
const selectAccountSubcategories = (state) => state.account.subcategories;
const selectAccountPanels = (state) => state.account.panels;
const selectAccountSelectedPanels = (state) => state.account.selectedPanels;
const selectWorkStream = (state) => state.account.stream;
const selectTabId = (state) => state.account.tabId;
const selectAccountLoading = (state) => state.account.loading;
const selectAccountError = (state) => state.account.error;
const selectPanelLoadingMap = (state) => state.account.panelLoadingMap;
const selectPanelLoadingStatus = (id) => (state) =>
  state.account.panelLoadingMap?.[id] ?? "not-loaded";
const selectAccountSelectedPanelById = (id) => (state) =>
  state.account.selectedPanels.find((panel) => panel.id === id);
const selectPage = (state) => state.account.page;

export const selectors = {
  selectAccount,
  selectAccountCategories,
  selectAccountSubcategories,
  selectAccountPanels,
  selectAccountLoading,
  selectAccountError,
  selectAccountSelectedPanels,
  selectPanelLoadingMap,
  selectPanelLoadingStatus,
  selectAccountSelectedPanelById,
  selectTabId,
  selectWorkStream,
  selectPage,
};

// TODO: should split queries thunks into a queries.js file.
const subcategoriesByCategoryId = (categoryId) => async (dispatch, _) => {
  if (!categoryId) {
    return Promise.resolve(); // No categoryId provided, nothing to fetch.
  }
  // TODO: should check if subcategories are cached prior to fetching.
  try {
    dispatch(actions.setLoading(true));
    const subcategories = await getPredifinedPanelSubcategoriesByCategoryId(
      categoryId
    );
    dispatch(actions.setSubCategories(subcategories));
    return subcategories;
  } catch (error) {
    dispatch(actions.setError(error));
  } finally {
    dispatch(actions.setLoading(false));
  }
};

const panels = async (dispatch, getState) => {
  const state = selectors.selectAccount(getState());
  const page = selectors.selectPage(getState());
  dispatch(actions.setLoading(true));

  const categories = state.categories
    .filter((c) => c.isSelected)
    .map((c) => c.id);
  const subcategories = state.subcategories
    .flatMap((group) => group.subCategories)
    .filter((s) => s.isSelected)
    .map((s) => s.id);

  try {
    const panels = await getPredifinedPanelsByFilter(
      page,
      10,
      state.dropdownValue ? state.dropdownValue : "",
      state.searchQuery ? state.searchQuery : "",
      {
        categories,
        subcategories,
      }
    );

    dispatch(actions.setPanels(panels));
  } catch (error) {
    console.error("Error fetching panels", error);
    dispatch(actions.setError(error));
  } finally {
    dispatch(actions.setLoading(false));
  }
};

const getPanelInitialInfo = () => async (dispatch, getState) => {
  const accountId = localStorage.getItem("u_account_id");
  if (!accountId) return;

  // dispatch(actions.setLoading(true));
  dispatch(loaderActions.showLoader());
  try {
    const response = await getPredifinedPanelCategories(accountId);
    dispatch(actions.setCategories(response));

    const categoriesSelected = response.filter(
      (category) => category.isSelected
    );

    const subcategoryPromises = categoriesSelected.map((category) =>
      dispatch(queries.subcategoriesByCategoryId(category.id))
    );

    await Promise.all(subcategoryPromises);

    await dispatch(queries.panels);
    // onSuccess();
  } catch (error) {
    console.error("Error getting panel initial info:", error);
  } finally {
    // dispatch(actions.setLoading(false));
    dispatch(loaderActions.hideLoader());
  }
};

const getPanelDataConfigurationByPanelId = (panel, config, apiData) => {
  if (panel.panelType === "Table") {
    const configuration = config.tableConfig;
    const metaData = config.tableColumns.map((column) => ({
      referencedColumnName: column.referencedColumnName,
      label: column.label,
      specificWidth: column.specificWidth,
      horizontalAlignment: column.horizontalAlignment,
      show: true,
    }));

    const data = apiData.data.map((cells) => {
      const initialValueAcc = {};
      const row = cells.reduce((acc, value, key) => {
        acc[apiData.columns[key]] = value;
        return acc;
      }, initialValueAcc);
      return row;
    });

    return {
      configuration,
      metaData,
      data,
      alternatingRowColor: configuration.alternatingRowColor,
      fitColumns: configuration.fitColumns,
      thresholdValues: configuration.thresholdValues || [],
    };
  }
  if (panel.panelType === "Pie") {
    const data = apiData.data.map((row) => {
      const obj = {};
      apiData.columns.forEach((column, index) => {
        obj[column] = row[index];
      });
      return obj;
    });

    return {
      metadata: config,
      data,
      showLegend: config.showLegend || false,
    };
  }
  if (panel.panelType === "XY Series") {
    const xyConfig = config.xyConfig;
    const xyColumns = config.xyColumns;

    // Transformar los datos del array format al object format
    const data = apiData.data.map((row) => {
      const obj = {};
      apiData.columns.forEach((column, index) => {
        obj[column] = row[index];
      });
      return obj;
    });

    // Separar las columnas por tipo de renderizado
    const columnFields = xyColumns
      .filter((col) => col.renderType === "bar")
      .map((col) => col.valueColumnName);

    const areaFields = xyColumns
      .filter((col) => col.renderType === "area")
      .map((col) => col.valueColumnName);

    const columnSeriesNames = xyColumns
      .filter((col) => col.renderType === "bar")
      .map((col) => col.label || col.valueColumnName);

    const areaSeriesNames = xyColumns
      .filter((col) => col.renderType === "area")
      .map((col) => col.label || col.valueColumnName);

    const metadata = {
      categoryField: xyConfig.axisColumnName,
      columnFields,
      areaFields,
      columnSeriesNames,
      areaSeriesNames,
      showLegend: true,
      showTooltip: true,
      // Configuraciones adicionales del xyConfig
      showVerticalGridLines: xyConfig.showVerticalGridLines,
      showHorizontalGridLines: xyConfig.showHorizontalGridLines,
      minValueAtZero: xyConfig.minValueAtZero,
      maxValueScaling: xyConfig.maxValueScaling,
    };

    return {
      metadata,
      data,
      showLegend: true,
      showTooltip: true,
    };
  }

  if (panel.panelType === "KPI") {
    const data = apiData.data.map((row) => {
      const obj = {};
      apiData.columns.forEach((column, index) => {
        obj[column] = row[index];
      });
      return obj;
    });

    // Transform comparison data if it exists
    let auxComparisonData = null;
    if (apiData.auxComparation && apiData.auxComparation.data) {
      auxComparisonData = apiData.auxComparation.data.map((row) => {
        const obj = {};
        apiData.auxComparation.columns.forEach((column, index) => {
          obj[column] = row[index];
        });
        return obj;
      });
    }

    return {
      metadata: config,
      data,
      auxComparisonData,
    };
  }
};

const getPanelDataRequestByType = (panel) => {
  if (typeof panel !== "object") return;
  if (!panel.datasourceId) return;
  if (typeof panel.panelType !== "string") return;
  if (panel.configuration) return; // already has configuration loaded

  switch (panel.panelType) {
    case "Table":
      return getTableConfigurationByPanelId;
    case "Pie":
      return getPieConfigurationByPanelId;
    case "XY Series":
      return getXyChartConfigurationByPanelId;
    case "KPI":
      return getKpiConfigurationByPanelId;
    default:
      console.warn(
        `No data request defined for panel type: ${panel.panelType}`
      );
      const delay = Math.random() * (5 - 1) + 1;
      return (_) => new Promise((resolve) => setTimeout(resolve, 1000 * delay));
  }
};

const loadPanelData = (panel) => async (dispatch) => {
  dispatch(actions.setPanelLoading({ panelId: panel.id, status: "loading" }));

  try {
    const request = getPanelDataRequestByType(panel);
    if (!request) return;
    const configResponse = await request(panel.id);
    const dataResponse = await getConsumeDataByPanelId(panel.id);

    const configuration = getPanelDataConfigurationByPanelId(
      panel,
      configResponse,
      dataResponse
    );

    console.log("configuration", { configuration });

    dispatch(actions.updatePanel({ panelId: panel.id, configuration }));
  } catch (error) {
    console.error(`Error loading panel ${panel}:`, error);
  } finally {
    dispatch(actions.setPanelLoading({ panelId: panel.id, status: "loaded" }));
  }
};

const loadAllPredefinedPanels = () => async (dispatch, getState) => {
  dispatch(actions.initializedLoadingMap());

  try {
    const panels = selectors.selectAccountPanels(getState());

    const panelsPromises = panels.map((panel) => {
      dispatch(queries.loadPanelData(panel));
    });

    await Promise.all(panelsPromises);
  } catch (error) {
    console.error("Error loading all predefined panels:", error);
  } finally {
  }
};

export const queries = {
  subcategoriesByCategoryId,
  panels,
  getPanelInitialInfo,
  loadPanelData,
  loadAllPredefinedPanels,
};

// TODO: should split mutation thunks into a mutations.js file.
const toggleCategory = (category) => async (dispatch, _) => {
  const mutation = category.isSelected
    ? setPredifinedPanelCategorySelectState
    : setPredifinedPanelCategoryUnSelectState;

  dispatch(actions.setLoading(true));

  try {
    await mutation(category.categoryId);
    dispatch(actions.setSelectCategory(category));
    if (category.isSelected) {
      dispatch(queries.subcategoriesByCategoryId(category.categoryId));
    }
    dispatch(queries.panels);
  } catch (error) {
    console.error("Error updating category:", error);
    dispatch(actions.setError(error));
  } finally {
    dispatch(actions.setLoading(false));
  }
};

const toggleSubcategory = (subcategory) => async (dispatch, _) => {
  const mutation = subcategory.isSelected
    ? setPredifinedPanelCategorySelectState
    : setPredifinedPanelCategoryUnSelectState;

  dispatch(actions.setLoading(true));

  try {
    await mutation(subcategory.subcategoryId);
    dispatch(actions.setSelectSubcategory(subcategory));
    dispatch(queries.panels);
  } catch (error) {
    console.error("Error updating subcategory:", error);
    dispatch(actions.setError(error));
  } finally {
    dispatch(actions.setLoading(false));
  }
};

export const mutations = {
  toggleCategory: toggleCategory,
  toggleSubcategory: toggleSubcategory,
};

export const accountReducer = accountSlice.reducer;
